// @flow
import React, {
  useEffect,
  useState,
  useLayoutEffect,
  useMemo,
  useCallback,
} from 'react';

import StickyActions from './StickyActions/StickyActions';
import {makeStyles, Typography, IconButton} from '@material-ui/core';
import Message from './Message/Message';

import BottomContent from './BottomContent/BottomContent';
import {actions as chatActions} from '../../../../duck';

import type {Theme} from '@material-ui/core';
import {colors} from '../../../../../../../assets/styles/colors';
import useOnConversationChange, {
  useCurrentConversation,
} from '../../../../utils/conversation';
import {
  useConversationAvailableStatus,
  useConversationMessages,
  useNextTokenArchived,
  useMessageReactions,
} from '../../../../utils/message';
import type {SendMessageParams} from '../../../../utils/message';
import {
  useNearScreen,
  useDebounceEffect,
} from '../../../../../../../utils/react';
import InlineLoader from '../../../../../../../elements/LoadingSpinner/InlineLoader';

import type {MessageFormFields} from '../../constants';
import PinnedContent from './PinnedContent';
import TimeLimitContent from './PinnedContent/TimeLimitContent';
import RetryConnectionContent from './PinnedContent/RetryConnectionContent';
import ChatActionBar from './ChatActionBar';
import ScheduleMessageNotification from './BottomNotification/ScheduleMessageNotification';
import tiendadaPatter from '../../../../../../../assets/img/tiendada/tiendada-pattern.svg';
import DisconnectMessageNotification from './BottomNotification/DisconnectNotification';
import {useStoreDevices} from '../../../../utils';
import ChangePhoneMessageNotification from './BottomNotification/ChangePhoneNotification';
import ArchivedMessageNotification from './TopNotification/ArchivedChat';
import MessageArchived from './Message/MessageArchived';
import {formatPhoneNumber} from '../../../../../../../utils/phone';
import BottomNotification from './BottomNotification';

import type {ConversationMessage} from '../../../../../../../entities';
import ForwardMessageModal from '../../../Modals/ForwardMessageModal';
import RequiresAssistanceNotification from './BottomNotification/RequiresAssistanceNotification';
import ReplyingMessage from './ReplyingMessage';
import {ExpandMore} from '@material-ui/icons';
import {useRef} from 'react';
import {useActions} from '../../../../../../../utils/globalStore';
import dayjs from 'dayjs';
import SystemMessage from './Message/SystemMessage';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    height: '100%',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
  },
  emptyState: {
    height: '100%',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '1rem',
  },
  messages: {
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    flexGrow: 1,
    backgroundImage: `url(${tiendadaPatter})`,
    backgroundColor: '#F6F6F6',
    backgroundRepeat: 'repeat',
  },
  messageList: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column-reverse',
    gap: '.75rem',
    overflowY: 'auto',
    height: '1px',
    paddingInline: '.75rem',
    position: 'relative',

    '&::-webkit-scrollbar': {
      width: '5px',
      height: '5px',
    },

    '&::-webkit-scrollbar-track': {
      background: colors.grey200,
      borderRadius: '5px',
    },

    '&::-webkit-scrollbar-thumb': {
      background: colors.grey400,
      borderRadius: '5px',
    },
  },
  footer: {
    position: 'relative',
    width: '100%',
    background: 'white',
  },
  overlayDisabled: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: `${colors.grey100}80}`,
    zIndex: 1,
    cursor: 'not-allowed',
    backdropFilter: 'blur(2px)',
  },
  inputMessageContainer: {
    position: 'relative',
  },
  loader: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    top: 0,
    left: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    zIndex: 1000,
    backdropFilter: 'blur(.5rem)',
  },
  scrollToBottomButton: {
    position: 'absolute',
    top: '-3.5rem',
    right: '.5rem',
    background: colors.grey200,
  },
}));

function groupByDay(messages) {
  return messages.reduce((acc, msg) => {
    const d = dayjs(msg.receivedAt);
    let label;

    if (d.isSame(dayjs(), 'day')) {
      label = 'Hoy';
    } else if (d.isSame(dayjs().subtract(1, 'day'), 'day')) {
      label = 'Ayer';
    } else {
      label = d.format('dddd DD, MMM YYYY');
    }

    acc[label] = acc[label] || [];
    acc[label].push(msg);
    return acc;
  }, {});
}

export type Props = {
  hideStickyActions?: boolean,
  useAppRootForScroll?: boolean,
};

const SERVICES_AVAILABLE_TO_RECONNECT = ['WHATSAPP_WEB'];

export default function ChatContent(props: Props) {
  const {hideStickyActions, useAppRootForScroll} = props;
  const classes = useStyles();

  const {currentConversation: conversation} = useCurrentConversation();
  const {prevTokenArchived} = useNextTokenArchived();
  const timeRestriction = useConversationAvailableStatus();

  const setAtChatEnd = useActions(chatActions.setIsAtChatEnd);

  const [
    messageToReply,
    setMessageToReply,
  ] = useState<ConversationMessage | null>(null);

  const [pinnedTimerOpen, setPinnedTimerOpen] = useState(false);
  const [hasPreviousConversation, setHasPreviousConversation] = useState(false);
  const [
    isFirstLoadArchivedMessages,
    setIsFirstLoadArchivedMessages,
  ] = useState(true);
  const [
    showTopNotificationChangePhone,
    setShowTopNotificationChangePhone,
  ] = useState(true);

  const [
    messageToForward,
    setMessageToForward,
  ] = useState<ConversationMessage | null>(null);

  const [showScrollButton, setShowScrollButton] = useState(false);
  const listRef = useRef<HTMLDivElement | null>(null);
  const prevScrollHeightRef = useRef(0);
  const prevScrollTopRef = useRef(0);
  const loadingMoreRef = useRef(false);

  const {
    sendMessage,
    messages,
    loadPrevPage,
    loadNextPage,
    addPageArchivedChat,
    loading,
    hasMoreMessages,
    reactToMessage,
    isSendingMessage,
    // navigateToToken,
    hasMoreNext,
  } = useConversationMessages(conversation ? conversation.id : '');

  const grouped = useMemo(() => {
    return groupByDay(messages.filter(m => !m.isArchived));
  }, [messages]);

  const groupedMap = useMemo(() => {
    return Object.entries(grouped).map(([day, msgs]) => ({
      day,
      messages: msgs,
    }));
  }, [grouped]);

  const {
    reactionMessages,
    removeReactionMessage,
    resetReactionMessages,
  } = useMessageReactions();

  const {storeDevices} = useStoreDevices({
    refetchOnMount: true,
  });

  const [highlightedMessageId, setHighlightedMessageId] = useState<
    string | null
  >(null);

  const handleSnippetClick = useCallback(
    (token: string, messageId: string) => {
      const messageExistInMessages = messages.some(
        message => message.id === messageId
      );

      if (messageExistInMessages) {
        const element = listRef.current?.querySelector(
          `[data-message-id="${messageId}"]`
        );
        if (element) {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        }
      } else {
        //TODO: Implement navigation to token
        // navigateToToken(token);
      }

      setHighlightedMessageId(messageId);
    },
    [messages]
  );

  useEffect(() => {
    if (!highlightedMessageId) return;
    const t = setTimeout(() => setHighlightedMessageId(null), 3000);
    return () => clearTimeout(t);
  }, [highlightedMessageId]);

  const {fromRef: topRef, isNearScreen: isNearTop} = useNearScreen({
    distance: '0px 0px 20px 0px',
    once: false,
    useAppRoot: useAppRootForScroll,
  });

  const {fromRef: bottomRef, isNearScreen: isNearBottom} = useNearScreen({
    distance: '0px 20px 0px 0px',
    once: false,
    useAppRoot: useAppRootForScroll,
  });

  useOnConversationChange(conversation, () => {
    setMessageToReply(null);
    setMessageToForward(null);
    resetReactionMessages();
  });

  const currentDevice = storeDevices.find(
    device =>
      conversation?.deviceId === device.id && device.state === 'CONNECTED'
  );

  const currentDeviceActive =
    storeDevices.find(
      device =>
        device.id === conversation?.deviceId && device.state === 'CONNECTED'
    ) ||
    storeDevices.find(
      device =>
        device.service === conversation?.ravenService &&
        device.state === 'CONNECTED'
    );

  const conversationCanReconnect =
    conversation?.deviceId !== currentDeviceActive?.id &&
    currentDeviceActive?.service === 'WHATSAPP_WEB';

  const whatsappDisconnectedDevice = storeDevices.find(
    device =>
      device.id === conversation?.deviceId && device.state === 'DISCONNECTED'
  );

  const handleScroll = e => {
    const {scrollTop} = e.currentTarget;
    setShowScrollButton(scrollTop !== 0);
    setAtChatEnd(!hasMoreNext);
  };

  const messageLength = messages.length || 0;

  useEffect(() => {
    if (!listRef.current) return;
    setAtChatEnd(!hasMoreNext);
  }, [messageLength, hasMoreNext, setAtChatEnd]);

  const handleSendMessage = async (params: MessageFormFields) => {
    if (!conversation) return;
    if (loading) return;
    const chatService = conversation.ravenService;

    if (chatService === 'WHATSAPP_WEB') {
      const messageParams: SendMessageParams = {
        messageType: params.type,
        files: params.files,
        message: params.message,
        attachment: params.attachment && params.attachment.src,
      };

      if (messageToReply && messageToReply.rawReceivedAt) {
        messageParams.repliedMessage = {
          messageId: messageToReply.id,
          messageReceivedAt: messageToReply.rawReceivedAt,
          messageType: messageToReply.messageType,
          text: messageToReply.text,
          fromMe: messageToReply.fromMe,
        };
      }

      await sendMessage(messageParams);

      if (messageToReply) {
        setMessageToReply(null);
      }
    }

    if (chatService === 'WHATSAPP_API') {
      await sendMessage({
        messageType: params.type,
        files: params.files,
        message: params.message,
        attachment: params.attachment && params.attachment.src,
      });
    }

    if (chatService === 'FB_MESSENGER' || chatService === 'INSTAGRAM') {
      const hasFiles = params.files && params.files.length > 0;
      const hasAttachment = params.attachment && params.attachment.src;
      const hasMessage = params.message && params.message.length > 0;

      const getRepliedMessage = () => {
        if (messageToReply && messageToReply.rawReceivedAt) {
          return {
            messageId: messageToReply.id,
            messageReceivedAt: messageToReply.rawReceivedAt,
            messageType: messageToReply.messageType,
            text: messageToReply.text,
            fromMe: messageToReply.fromMe,
          };
        }
        return undefined;
      };

      if (hasMessage) {
        await sendMessage({
          messageType: 'TEXT',
          message: params.message,
          repliedMessage: getRepliedMessage(),
        });
      }
      if (hasFiles || hasAttachment) {
        await sendMessage({
          messageType: params.type,
          files: params.files,
          attachment: params.attachment && params.attachment.src,
          repliedMessage: !hasMessage ? getRepliedMessage() : undefined,
        });
      }

      if (messageToReply) {
        setMessageToReply(null);
      }
    }
  };

  // Only call when scroll detection flag is fired
  useDebounceEffect(
    () => {
      if (!isNearTop) return;
      loadPrevPage();
      if (
        !isFirstLoadArchivedMessages &&
        conversation &&
        conversation?.previousConversationId !== null
      ) {
        addPageArchivedChat(
          // $FlowFixMe
          conversation?.previousConversationId,
          prevTokenArchived
        );
      }
    },
    750,
    [isNearTop, isFirstLoadArchivedMessages]
  );

  const handleLoadNextPage = () => {
    const el = listRef.current;
    if (!el || !hasMoreNext) return;

    prevScrollHeightRef.current = el.scrollHeight;
    prevScrollTopRef.current = el.scrollTop;
    loadingMoreRef.current = true;

    loadNextPage();
  };

  useLayoutEffect(() => {
    if (!loading && loadingMoreRef.current) {
      const el = listRef.current;
      if (el) {
        const newHeight = el.scrollHeight;
        const diff = newHeight - prevScrollHeightRef.current;
        el.scrollTop = prevScrollTopRef.current + diff;
      }
      loadingMoreRef.current = false;
    }
  }, [loading, messages.length]);

  useDebounceEffect(
    () => {
      if (isNearBottom) {
        handleLoadNextPage();
      }
    },
    500,
    [isNearBottom]
  );

  useEffect(() => {
    const statePreviousConversation =
      hasMoreMessages === false &&
      conversation?.previousConversationId &&
      conversation?.previousConversationId !== null;
    setHasPreviousConversation(statePreviousConversation);
  }, [conversation, hasMoreMessages, currentDevice]);

  useEffect(() => {
    if (!conversation) return;
    if (!timeRestriction) return;

    setPinnedTimerOpen(true);
  }, [conversation, timeRestriction]);

  useEffect(() => {
    setShowTopNotificationChangePhone(true);
  }, [conversation]);

  const handleReactMessage = useCallback(
    (message: ConversationMessage, emoji: string) => {
      reactToMessage(message, emoji).then(() => {
        const myReaction = reactionMessages.find(
          reaction => reaction.messageId === message.id && reaction.fromMe
        );

        if (myReaction) {
          removeReactionMessage(myReaction.id);
        }
      });
    },
    [reactToMessage, reactionMessages, removeReactionMessage]
  );

  const handleRemoveReaction = useCallback(
    (message: ConversationMessage, reactionId: string) => {
      reactToMessage(message, '').then(() => {
        removeReactionMessage(reactionId);
      });
    },
    [reactToMessage, removeReactionMessage]
  );

  const handleReplyMessage = useCallback(
    (message: ConversationMessage) => {
      if (!conversation) return;
      setMessageToReply(message);
    },
    [conversation]
  );

  if (!conversation) return null;

  const canReconnect = SERVICES_AVAILABLE_TO_RECONNECT.includes(
    conversation.ravenService
  );

  if (!currentDevice && !canReconnect) {
    return (
      <div className={classes.emptyState}>
        <div>
          <Typography variant="body1" color="textSecondary" align="center">
            No hay un dispositivo conectado para esta conversación.
          </Typography>
          <Typography variant="body2" color="textSecondary" align="center">
            Dispositivo de la conversación:{' '}
            <strong>{conversation?.deviceId || 'N/A'}</strong>
          </Typography>
        </div>
        <div>
          <Typography variant="body1" color="textSecondary" align="center">
            Dispositivos conectados:
          </Typography>

          {storeDevices.map(device => (
            <Typography
              key={device.id}
              variant="body2"
              color="textSecondary"
              align="center"
            >
              {device.identifier} <strong>({device.id})</strong> - Estado:{' '}
              {device.state}
            </Typography>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      {!conversation ||
        (whatsappDisconnectedDevice && (
          <div className={classes.overlayDisabled}></div>
        ))}
      {!hideStickyActions && <StickyActions />}
      {timeRestriction && (
        <PinnedContent open={pinnedTimerOpen}>
          <TimeLimitContent onClose={() => setPinnedTimerOpen(false)} />
        </PinnedContent>
      )}

      <RetryConnectionContent />

      <div className={classes.messages}>
        {showTopNotificationChangePhone &&
          !hideStickyActions &&
          hasPreviousConversation &&
          conversation?.id &&
          !prevTokenArchived && (
            <ArchivedMessageNotification
              handleHiddenButton={() => {
                setShowTopNotificationChangePhone(false);
                setIsFirstLoadArchivedMessages(false);
              }}
            />
          )}

        <div
          className={classes.messageList}
          onScroll={handleScroll}
          ref={listRef}
        >
          {!loading && (
            /* because has column-reverse, we need to add a top ref to scroll to the top */
            <span
              ref={bottomRef}
              style={{
                minHeight: '1px',
              }}
            ></span>
          )}

          {groupedMap.map(({day, messages}) => (
            <React.Fragment key={day}>
              {messages.map(message => (
                <div key={message.id} data-message-id={message.id}>
                  <Message
                    message={message}
                    highlighted={message.id === highlightedMessageId}
                    reactions={reactionMessages}
                    onForward={setMessageToForward}
                    onReact={handleReactMessage}
                    onClickRepliedMessage={handleSnippetClick}
                    onReply={handleReplyMessage}
                    onRemoveReaction={handleRemoveReaction}
                  />
                </div>
              ))}
              <SystemMessage message={day} />
            </React.Fragment>
          ))}

          {!hasMoreMessages && conversation?.previousConversationId && (
            <span
              style={{
                width: 'fit-content',
                margin: '0 auto',
                background: 'white',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '0.5rem',
                color: colors.grey700,
                borderRadius: '8px',
              }}
            >
              Conversación nueva{' '}
              {currentDevice && formatPhoneNumber(currentDevice?.identifier)}
            </span>
          )}

          {messages.some(message => message.isArchived) && (
            <>
              {messages
                .filter(message => message.isArchived)
                .map((message, index) => (
                  <MessageArchived key={message.id} message={message} />
                ))}

              <span
                style={{
                  width: 'fit-content',
                  margin: '0 auto',
                  background: 'white',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: '0.5rem',
                  color: colors.grey700,
                  borderRadius: '8px',
                }}
              >
                Conversación archivada
              </span>
            </>
          )}

          {!loading && (
            /* because has column-reverse, we need to add a top ref to scroll to the top */
            <span
              ref={topRef}
              style={{
                minHeight: '1px',
              }}
            ></span>
          )}
          <InlineLoader loading={loading} size={24} />
        </div>
        <aside className={classes.footer}>
          {currentDevice?.state === 'CONNECTED' && (
            <>
              {showScrollButton && (
                <IconButton
                  className={classes.scrollToBottomButton}
                  onClick={() => {
                    if (listRef.current) {
                      listRef.current.scrollTo({
                        top: listRef.current.scrollHeight,
                        behavior: 'smooth',
                      });
                    }
                  }}
                >
                  <ExpandMore />
                </IconButton>
              )}
              <ScheduleMessageNotification />
              {conversation?.requiresAssistance && (
                <RequiresAssistanceNotification />
              )}
              <ChatActionBar />
              {messageToReply && (
                <ReplyingMessage
                  message={messageToReply}
                  onClear={() => setMessageToReply(null)}
                />
              )}
              <div className={classes.inputMessageContainer}>
                <BottomContent
                  onSendMessage={handleSendMessage}
                  isReplying={!!messageToReply}
                  disabled={!!timeRestriction && !timeRestriction.isAvailable}
                />
                {isSendingMessage && (
                  <InlineLoader loading size={24} className={classes.loader} />
                )}
              </div>
            </>
          )}
          {currentDevice?.state === 'DISCONNECTED' && (
            <BottomNotification
              color="warning"
              label="El dispositivo se ha desconectado"
            />
          )}

          {currentDeviceActive?.state === 'ARCHIVED' && (
            <BottomNotification
              color="danger"
              label="El dispositivo está eliminado"
            />
          )}

          {whatsappDisconnectedDevice && <DisconnectMessageNotification />}
          {conversationCanReconnect && conversation && conversation?.id && (
            <ChangePhoneMessageNotification
              nextConversationId={conversation?.nextConversationId || null}
              showInitConversation={!hideStickyActions}
            />
          )}
        </aside>
      </div>

      {messageToForward && (
        <ForwardMessageModal
          open
          message={messageToForward}
          onClose={() => setMessageToForward(null)}
        />
      )}
    </div>
  );
}
